import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Divider,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormHelperText,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';
import { ServiceDefinition } from '../../models';

// Helper function to format hours display
const formatHours = (minutes: number): string => {
  const hours = minutes / 60;
  const formatted = hours.toFixed(1);
  // Remove trailing .0 for whole numbers
  return formatted.endsWith('.0') ? formatted.substring(0, formatted.length - 2) : formatted;
};

interface ServiceDefinitionFormProps {
  services: ServiceDefinition[];
  onServicesChange: (services: ServiceDefinition[]) => void;
}

interface ServiceFormData {
  id: string;
  name: string;
  durationMinutes: number;
  color: string;
  launchRequired: boolean;
  launchMinHeight: number;
  retrievalRequired: boolean;
  retrievalMinHeight: number;
  earliestStart: string;
  latestStart: string;
  latestEnd: string;
}

/**
 * Component for managing service definitions including tide and time restrictions
 */
const ServiceDefinitionForm: React.FC<ServiceDefinitionFormProps> = ({
  services,
  onServicesChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingService, setEditingService] = useState<ServiceFormData | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Generate duration options (0.5 to 24 hours in 0.5 increments)
  const durationOptions = Array.from({ length: 48 }, (_, i) => (i + 1) * 0.5);

// Array of colors for default color selection
  const SERVICE_COLORS = [
    '#f44336', // red
    '#2196f3', // blue
    '#4caf50', // green
    '#ff9800', // orange
    '#9c27b0', // purple
    '#00bcd4', // cyan
    '#e91e63', // pink
    '#ffeb3a', // yellow
    '#009688', // teal
    '#673ab7', // deep purple
    '#3f51b5', // indigo
    '#8bc34a', // light green
    '#cddc39', // lime
    '#ffc107', // amber
    '#795548', // brown
    '#607d8b'  // blue grey
  ];

  // Initialize form data for a new service
  const initNewService = (): ServiceFormData => {
    // Pick a random color from the array as default
    const defaultColor = SERVICE_COLORS[Math.floor(Math.random() * SERVICE_COLORS.length)];
    
    return {
      id: uuidv4(),
      name: '',
      durationMinutes: 120, // Default 2 hours
      color: defaultColor,
      launchRequired: true,
      launchMinHeight: 1.8,
      retrievalRequired: true,
      retrievalMinHeight: 1.8,
      earliestStart: '08:00',
      latestStart: '16:00',
      latestEnd: '18:00',
    };
  };

  // Open dialog for adding a new service
  const handleAddService = () => {
    setEditingService(initNewService());
    setValidationErrors({});
    setDialogOpen(true);
  };

  // Open dialog for editing an existing service
  const handleEditService = (service: ServiceDefinition) => {
    setEditingService({
      id: service.id,
      name: service.name,
      durationMinutes: service.durationMinutes,
      color: service.color || SERVICE_COLORS[0], // Use existing color or default to first color
      launchRequired: service.tideRestrictions.launchRequired,
      launchMinHeight: service.tideRestrictions.launchMinHeight,
      retrievalRequired: service.tideRestrictions.retrievalRequired,
      retrievalMinHeight: service.tideRestrictions.retrievalMinHeight,
      earliestStart: service.timeRestrictions.earliestStart,
      latestStart: service.timeRestrictions.latestStart,
      latestEnd: service.timeRestrictions.latestEnd,
    });
    setValidationErrors({});
    setDialogOpen(true);
  };

  // Handle form field changes
  const handleFormChange = (field: keyof ServiceFormData, value: any) => {
    if (!editingService) return;

    setEditingService({
      ...editingService,
      [field]: value,
    });

    // Clear validation error for the field
    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: '',
      });
    }
  };

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!editingService!.name.trim()) {
      errors.name = 'Service name is required';
    }

    if (editingService!.durationMinutes <= 0) {
      errors.durationMinutes = 'Duration must be greater than 0';
    }

    if (editingService!.launchRequired && editingService!.launchMinHeight <= 0) {
      errors.launchMinHeight = 'Launch height must be greater than 0';
    }

    if (editingService!.retrievalRequired && editingService!.retrievalMinHeight <= 0) {
      errors.retrievalMinHeight = 'Retrieval height must be greater than 0';
    }

    // Check time restrictions
    const earliestStart = editingService!.earliestStart.split(':').map(Number);
    const latestStart = editingService!.latestStart.split(':').map(Number);
    const latestEnd = editingService!.latestEnd.split(':').map(Number);

    const earliestStartMinutes = earliestStart[0] * 60 + earliestStart[1];
    const latestStartMinutes = latestStart[0] * 60 + latestStart[1];
    const latestEndMinutes = latestEnd[0] * 60 + latestEnd[1];

    if (latestStartMinutes < earliestStartMinutes) {
      errors.latestStart = 'Latest start must be after earliest start';
    }

    if (latestEndMinutes < latestStartMinutes) {
      errors.latestEnd = 'Latest end must be after latest start';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save service definition
  const handleSaveService = () => {
    if (!editingService || !validateForm()) return;

    const serviceDefinition: ServiceDefinition = {
      id: editingService.id,
      name: editingService.name,
      durationMinutes: editingService.durationMinutes,
      color: editingService.color,
      tideRestrictions: {
        launchRequired: editingService.launchRequired,
        launchMinHeight: editingService.launchMinHeight,
        retrievalRequired: editingService.retrievalRequired,
        retrievalMinHeight: editingService.retrievalMinHeight,
      },
      timeRestrictions: {
        earliestStart: editingService.earliestStart,
        latestStart: editingService.latestStart,
        latestEnd: editingService.latestEnd,
      },
    };

    // Update services array
    const updatedServices = services.some(s => s.id === serviceDefinition.id)
      ? services.map(s => (s.id === serviceDefinition.id ? serviceDefinition : s))
      : [...services, serviceDefinition];

    onServicesChange(updatedServices);

    // Service saved to the services state, no longer storing as template

    // Close dialog
    setDialogOpen(false);
    setEditingService(null);
  };

  // Delete a service
  const handleDeleteService = (serviceId: string) => {
    const updatedServices = services.filter(s => s.id !== serviceId);
    onServicesChange(updatedServices);
    // Service removed from services state, no longer storing as template
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Service Definitions</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddService}
        >
          Add Service
        </Button>
      </Box>

      {services.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No services defined. Click "Add Service" to create one.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {services.map(service => (
            <Grid item xs={12} sm={6} md={4} key={service.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box 
                      sx={{ 
                        width: 16, 
                        height: 16, 
                        borderRadius: '50%', 
                        bgcolor: service.color || '#666',
                        mr: 1 
                      }}
                    />
                    <Typography variant="h6" sx={{ display: 'inline' }}>
                      {service.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Duration: {formatHours(service.durationMinutes)} {service.durationMinutes / 60 === 1 ? 'hour' : 'hours'}
                  </Typography>
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography variant="subtitle2">Tide Restrictions:</Typography>
                  {service.tideRestrictions.launchRequired && (
                    <Typography variant="body2">
                      Launch: Min {service.tideRestrictions.launchMinHeight}m
                    </Typography>
                  )}
                  {service.tideRestrictions.retrievalRequired && (
                    <Typography variant="body2">
                      Retrieval: Min {service.tideRestrictions.retrievalMinHeight}m
                    </Typography>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography variant="subtitle2">Time Restrictions:</Typography>
                  <Typography variant="body2">
                    {service.timeRestrictions.earliestStart} - {service.timeRestrictions.latestStart}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton 
                    aria-label="edit" 
                    size="small" 
                    onClick={() => handleEditService(service)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    aria-label="delete" 
                    size="small" 
                    onClick={() => handleDeleteService(service.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Service Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingService && editingService.id && services.some(s => s.id === editingService.id)
            ? 'Edit Service'
            : 'Add Service'}
        </DialogTitle>
        <DialogContent>
          {editingService && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Service Name"
                  value={editingService.name}
                  onChange={e => handleFormChange('name', e.target.value)}
                  fullWidth
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!validationErrors.durationMinutes}>
                  <InputLabel id="duration-select-label">Duration (hours)</InputLabel>
                  <Select
                    labelId="duration-select-label"
                    value={editingService.durationMinutes > 0 ? editingService.durationMinutes / 60 : ''} // Handle 0 or negative case if needed
                    label="Duration (hours)"
                    onChange={(e) => {
                      const hours = Number(e.target.value);
                      handleFormChange('durationMinutes', hours * 60);
                    }}
                  >
                    {durationOptions.map(hours => (
                      <MenuItem key={hours} value={hours}>
                        {hours} {hours === 1 ? 'hour' : 'hours'}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.durationMinutes && <FormHelperText>{validationErrors.durationMinutes}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="service-color-label">Service Color</InputLabel>
                  <Select
                    labelId="service-color-label"
                    id="service-color-select"
                    value={editingService.color}
                    onChange={(e) => handleFormChange('color', e.target.value)}
                    label="Service Color"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            mr: 1,
                            backgroundColor: selected as string
                          }}
                        />
                        <Typography variant="body2">
                          {selected}
                        </Typography>
                      </Box>
                    )}
                  >
                    {SERVICE_COLORS.map((color) => (
                      <MenuItem key={color} value={color}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              mr: 1,
                              backgroundColor: color
                            }}
                          />
                          <Typography variant="body2">
                            {color}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Tide Restrictions
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editingService.launchRequired}
                            onChange={e => handleFormChange('launchRequired', e.target.checked)}
                          />
                        }
                        label="Launch Tide Height Restriction"
                      />
                      <TextField
                        label="Minimum Launch Height (meters)"
                        type="number"
                        value={editingService.launchMinHeight}
                        onChange={e => handleFormChange('launchMinHeight', parseFloat(e.target.value))}
                        disabled={!editingService.launchRequired}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        }}
                        error={!!validationErrors.launchMinHeight}
                        helperText={validationErrors.launchMinHeight}
                        sx={{ mt: 1 }}
                      />
                    </FormGroup>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={editingService.retrievalRequired}
                            onChange={e => handleFormChange('retrievalRequired', e.target.checked)}
                          />
                        }
                        label="Retrieval Tide Height Restriction"
                      />
                      <TextField
                        label="Minimum Retrieval Height (meters)"
                        type="number"
                        value={editingService.retrievalMinHeight}
                        onChange={e => handleFormChange('retrievalMinHeight', parseFloat(e.target.value))}
                        disabled={!editingService.retrievalRequired}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">m</InputAdornment>,
                        }}
                        error={!!validationErrors.retrievalMinHeight}
                        helperText={validationErrors.retrievalMinHeight}
                        sx={{ mt: 1 }}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Time Restrictions
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Earliest Start Time"
                      type="time"
                      value={editingService.earliestStart}
                      onChange={e => handleFormChange('earliestStart', e.target.value)}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={!!validationErrors.earliestStart}
                      helperText={validationErrors.earliestStart}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Latest Start Time"
                      type="time"
                      value={editingService.latestStart}
                      onChange={e => handleFormChange('latestStart', e.target.value)}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={!!validationErrors.latestStart}
                      helperText={validationErrors.latestStart}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Latest End Time"
                      type="time"
                      value={editingService.latestEnd}
                      onChange={e => handleFormChange('latestEnd', e.target.value)}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={!!validationErrors.latestEnd}
                      helperText={validationErrors.latestEnd}
                    />
                  </Grid>
                </Grid>
                <FormHelperText>
                  Time restrictions define when the service can be scheduled.
                </FormHelperText>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSaveService} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceDefinitionForm;
